/**
 * Design tokens for Builder.io section components.
 * Single source of truth for spacing, typography, radii, breakpoints,
 * fonts, and colors used across BuilderLayout/components/*.
 * Mirrors V2 tokens in src/components/Common/HeroSectionV2/tokens.ts.
 */

export const SPACING = {
  none: '0',
  xxs: '4px',
  xs: '8px',
  sm: '12px',
  md: '16px',
  lg: '20px',
  xl: '24px',
  '2xl': '32px',
  '3xl': '40px',
  '4xl': '48px',
  '5xl': '64px',
  '6xl': '80px',
  '7xl': '96px',
};

export const CONTAINER = {
  maxWidth: '1280px',
  gutter: SPACING.xl,
  gutterMobile: SPACING.md,
};

/**
 * Content-width tier. Use these instead of magic numbers when setting per-component
 * `contentMaxWidth` defaults so every section visually aligns to the same outer
 * frame while prose paragraphs stay legible.
 *
 * - `reading`: prose blocks (Story: Centered body, paragraph copy). 60–75 chars/line.
 * - `frame`:   page-level alignment frame. Every section's L/R edge lands here.
 * - `full`:    full-bleed sections (Hero, Banner, Footer).
 *
 * Unitless numbers — call sites multiply or template into `${value}px`.
 */
export const CONTENT_WIDTH = {
  reading: 720,
  frame: 1080,
  full: 1280,
};

export const SECTION_PADDING = {
  standard: {
    desktop: '96px',
    mobile: '64px',
  },
  compact: {
    desktop: '64px',
    mobile: '48px',
  },
  tight: {
    desktop: '48px',
    mobile: '32px',
  },
  hero: {
    desktop: '24px 24px 64px',
    mobile: '24px 24px 48px',
  },
};

export const SECTION_RHYTHM = {
  headerToBody: SPACING['4xl'],
  headerToBodyMobile: SPACING['2xl'],
  betweenBlocks: SPACING['2xl'],
  betweenBlocksMobile: SPACING.xl,
  afterEyebrow: SPACING.sm,
  afterTitle: SPACING.md,
};

export const TYPOGRAPHY = {
  eyebrow: {
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
  },
  sectionTitle: {
    desktop: { fontSize: '48px', fontWeight: 400, lineHeight: 1.15 },
    mobile: { fontSize: '36px', fontWeight: 400, lineHeight: 1.2 },
  },
  sectionSubtitle: {
    desktop: { fontSize: '18px', lineHeight: 1.6 },
    mobile: { fontSize: '16px', lineHeight: 1.6 },
  },
  blockTitle: {
    desktop: { fontSize: '20px', fontWeight: 700, lineHeight: 1.3 },
    mobile: { fontSize: '18px', fontWeight: 700, lineHeight: 1.3 },
  },
  body: {
    desktop: { fontSize: '16px', lineHeight: 1.65 },
    mobile: { fontSize: '15px', lineHeight: 1.65 },
  },
  bodySmall: {
    fontSize: '14px',
    lineHeight: 1.6,
  },
  caption: {
    fontSize: '12px',
    lineHeight: 1.5,
  },
  statValue: {
    desktop: { fontSize: '30px', fontWeight: 600, lineHeight: 1.2 },
    mobile: { fontSize: '24px', fontWeight: 600, lineHeight: 1.2 },
  },
  statLabel: {
    desktop: { fontSize: '14px', lineHeight: 1.5 },
    mobile: { fontSize: '13px', lineHeight: 1.5 },
  },
};

export const FONT_FAMILY = {
  display: "var(--bld-font-display, 'owners-wide', 'Owners Wide', system-ui, sans-serif)",
  body: "var(--bld-font-body, 'owners-text', 'Owners Text', system-ui, sans-serif)",
};

export const RADIUS = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '24px',
  pill: '30px',
  full: '9999px',
};

export const BREAKPOINT = {
  sm: 600,
  md: 900,
  lg: 1100,
  xl: 1280,
};

/**
 * Pre-baked media query strings. Use for styled() calls without theme access.
 * Boundary values match MUI's theme.breakpoints.{up,down}() (exclusive on
 * down, +0.05px on up) so the two systems are interchangeable at all viewport
 * widths. Prefer theme.breakpoints when theme is available.
 */
export const MEDIA = {
  downSm: `@media (max-width: ${BREAKPOINT.sm - 0.05}px)`,
  downMd: `@media (max-width: ${BREAKPOINT.md - 0.05}px)`,
  downLg: `@media (max-width: ${BREAKPOINT.lg - 0.05}px)`,
  upMd: `@media (min-width: ${BREAKPOINT.md}px)`,
  upLg: `@media (min-width: ${BREAKPOINT.lg}px)`,
};

/**
 * Pre-baked container query strings (PD-34303). Mirror the `MEDIA` viewport
 * queries but use the `@container` axis so styles respond to the parent
 * container's rendered width rather than the viewport's. Required for any
 * Builder section that needs to look "mobile" inside narrow render contexts
 * where the viewport is desktop-sized but the content area is constrained:
 *
 *   - The Builder.io editor's split-pane preview (~250-300px content area)
 *   - Sidebars / multi-column layouts (BuilderClassicTwoColumnLayout etc.)
 *   - Any future embed where a section renders narrow on a desktop viewport
 *
 * Pair with `containerType: 'inline-size'` on the section's outermost
 * element (or `ContentWrapper`) so the query has a container to bind to.
 *
 * Pattern (declare both viewport AND container queries pointing at the
 * same rule so real mobile devices keep using the viewport path while
 * narrow render contexts trigger via the container path):
 *
 *   [MEDIA.downSm]:           mobileSizingRule,
 *   [CONTAINER_QUERY.downSm]: mobileSizingRule,
 *
 * Browser support: container queries are Safari 16+ / Chrome 105+ /
 * Firefox 110+ — all 2023+. Older browsers fall back gracefully to
 * viewport-only behavior.
 *
 * Naming: `CONTAINER_QUERY` (not `CONTAINER`) because the existing
 * `CONTAINER` export above this is a layout-sizing token (`maxWidth`,
 * `gutter`, etc.). Distinct concerns, distinct names.
 */
export const CONTAINER_QUERY = {
  downSm: `@container (max-width: ${BREAKPOINT.sm - 0.05}px)`,
  downMd: `@container (max-width: ${BREAKPOINT.md - 0.05}px)`,
  downLg: `@container (max-width: ${BREAKPOINT.lg - 0.05}px)`,
};

export const SHADOW = {
  card: '0 2px 12px -2px rgba(0, 0, 0, 0.08)',
  cardHover: '0 8px 30px -6px rgba(0, 0, 0, 0.12)',
  buttonHover: '0 6px 20px rgba(0, 0, 0, 0.15)',
};

export const COLORS = {
  primary: 'var(--bld-primary, #185C65)',
  primaryDark: 'var(--bld-primaryDark, #0F3B40)',
  primaryTint: 'var(--bld-primaryTint, rgba(24, 92, 101, 0.06))',
  accent: 'var(--bld-accent, #01D1B8)',
  accentOrange: 'var(--bld-accentOrange, #F47F2B)',
  text: 'var(--bld-text, #020E0B)',
  textMuted: 'var(--bld-textMuted, #577275)',
  border: 'var(--bld-border, #e0ddd8)',
  borderMuted: 'var(--bld-borderMuted, #DCE4E5)',
  background: 'var(--bld-background, #FDFDFC)',
  backgroundAlt: 'var(--bld-backgroundAlt, #FBF8F3)',
  white: 'var(--bld-white, #FFFFFF)',
};

/**
 * Editorial "se3" reskin token overrides (PD — se3 Builder variant).
 *
 * Plain object of `--bld-*` CSS custom properties mapped to the HomepageV3
 * editorial palette (see src/components/HomepageV3/theme/tokens.ts). Applied
 * as inline `style` on the Builder layout root ONLY when the active Builder
 * variant === 'se3'. Every other variant (including `control`) renders with
 * no wrapper style, so the `var(--bld-*, <literal>)` fallbacks above resolve
 * to the original literal values — byte-for-byte unchanged.
 *
 * Colors + fonts only (styling-only tokens). SPACING/RADIUS/BREAKPOINT/
 * CONTAINER numeric values are untouched because they feed JS math.
 */
export const EDITORIAL_TOKEN_VARS = {
  '--bld-primary': '#0C6F64',
  '--bld-primaryDark': '#0B292D',
  '--bld-primaryTint': 'rgba(11, 41, 45, 0.06)',
  '--bld-accent': '#0C6F64',
  '--bld-accentOrange': '#8C6A2C',
  '--bld-text': '#221E19',
  '--bld-textMuted': '#514B40',
  '--bld-border': '#E8E1D6',
  '--bld-borderMuted': '#E8E1D6',
  '--bld-background': '#FCF9F5',
  '--bld-backgroundAlt': '#F5EFE4',
  '--bld-white': '#FFFFFF',
  '--bld-font-display': "var(--font-ibm-plex-serif), 'IBM Plex Serif', Georgia, serif",
  '--bld-font-body': "var(--font-ibm-plex-sans), 'IBM Plex Sans', system-ui, sans-serif",
};

export const sectionBase = (theme) => ({
  width: '100%',
  position: 'relative',
  boxSizing: 'border-box',
  padding: `${SECTION_PADDING.standard.desktop} ${CONTAINER.gutter}`,
  [theme.breakpoints.down('md')]: {
    padding: `${SECTION_PADDING.standard.mobile} ${CONTAINER.gutterMobile}`,
  },
});

export const contentWrapperBase = {
  width: '100%',
  maxWidth: CONTAINER.maxWidth,
  margin: '0 auto',
  boxSizing: 'border-box',
};
